import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Box, CircularProgress, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";

import Loader from "../Loader/LoaderFile";
import WidgetCardContainer from "components/Widgets/WidgetCardContainer";
import { useLocation } from "react-router-dom";

import PlatformHealthWidget from "./PlatformHealthWidget";
import PlatformHealthModal from "./PlatformHealthModal";
import { PlatformHealthSummaryResponse, PlatformWidget } from "./types";
import { SEVERITY, TIMEZONE_MAP } from "./Constants";
import { getTopSegments, deriveStatusKey } from "./PlatformHealthUtils";
import PlatformHealthMetricsPortal from "./PlatformHealthMetricsPortal";
import { getPlatformHealthSummary } from "service/service";
import moment from "moment";
import {
  DEFAULT_TIME_RANGE,
  MetricKey,
  mapResponseToPlatformWidgets,
  widgetMatchesSearch,
  getDominantTopSeg,
  getSectionPlaceholderCount,
  buildSummaryAggregate,
} from "./helpers";
import {
  dashboardHeaderTextSx,
  dashboardSectionHeaderSx,
  placeholderCardSx,
} from "./styles";
import { usePersistentDashboardSettings } from "../../hooks/usePersistentDashboardSettings";

// Module-level, not component state — persists across renders/remounts within this page load
let dashboardConfigCache: {
  totalPlatforms: number;
  platforms: any[];
  availableDates: any[];
} | null = null;

function buildTimeRangeBody(
  timeRange: string,
  customStartDate: Date | null,
  customEndDate: Date | null
): any {
  const ianaTimezone = TIMEZONE_MAP.CST;

  if (timeRange === "custom" && customStartDate && customEndDate) {
    return {
      timeRange: {
        startTime: moment(customStartDate)
          .tz(ianaTimezone)
          .startOf("day")
          .format("MM-DD-YYYY HH:mm:ss"),
        endTime: moment(customEndDate)
          .tz(ianaTimezone)
          .endOf("day")
          .format("MM-DD-YYYY HH:mm:ss"),
      },
    };
  }
  if (timeRange === "today") {
    return {
      timeRange: {
        startTime: moment.tz(ianaTimezone).startOf("day").format("MM-DD-YYYY HH:mm:ss"),
        endTime: moment.tz(ianaTimezone).endOf("day").format("MM-DD-YYYY HH:mm:ss"),
      },
    };
  }
  return { timeRange };
}

const PlatformHealthWidgetSkeleton = () => (
  <Box
    sx={{
      minHeight: 358,
      border: "1px solid #ddd",
      borderRadius: 1,
      p: 2,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <CircularProgress size={30} />
  </Box>
);

const PlatformHealthDashboard = () => {

  const {
    autoRefreshInterval: persistedAutoRefresh,
    activeStatusFilter: persistedStatusFilter,
    searchText: persistedSearchText,
    timeRange: persistedTimeRange,
    customStartDate: persistedCustomStartDate,
    customEndDate: persistedCustomEndDate,
    isLoaded: settingsLoaded,
    updateAutoRefresh,
    updateStatusFilter,
    updateSearch,
    updateTimeRange,
  } = usePersistentDashboardSettings();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PlatformHealthSummaryResponse | null>(null);

  const [timeRange, setTimeRange] = useState<string>(persistedTimeRange || DEFAULT_TIME_RANGE);
  const [searchText, setSearchText] = useState<string>(persistedSearchText || "");

  const [userActionFetching, setUserActionFetching] = useState(false);

  const pendingActionRef = useRef<"search" | "clearIcon" | "clearAll" | null>(null);
  const isMountedRef = useRef(true);
  const isInitialFetchRef = useRef(true);

  const [triggerFetch, setTriggerFetch] = useState(0);

  const [customStartDate, setCustomStartDate] = useState<Date | null>(persistedCustomStartDate || null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(persistedCustomEndDate || null);

  const latestWidgetsRef = useRef<(PlatformWidget | null)[]>([]);
  latestWidgetsRef.current = data?.platformWidgets ?? [];

  const defaultDataRef = useRef<PlatformHealthSummaryResponse | null>(null);

  const latestSearchTextRef = useRef<string>("");
  latestSearchTextRef.current = searchText;

  const isBackendSearch = (text: string) => {
    const q = text.trim().toLowerCase();
    if (!q) return false;

    if (!data || latestWidgetsRef.current.length === 0) {
      return false;
    }

    // 1. If it matches a platform name loaded on frontend, handle on frontend
    const isPlatformMatch = latestWidgetsRef.current.some((w) =>
      w && (w.platformName || "").toLowerCase().includes(q)
    );
    if (isPlatformMatch) return false;

    // 2. If it matches any widget's KPI/alert content already loaded, handle on frontend
    const isContentMatch = latestWidgetsRef.current.some((w) => w && 
      widgetMatchesSearch(w, q)
    );
    if (isContentMatch) return false;

    // 3. If query is very short, don't trigger backend (prevents lag while typing)
    if (q.length < 3) return false;

    // 4. Otherwise, attempt a backend search for platforms not currently in the visible list
    return true;
  };

  const [activeStatusFilter, setActiveStatusFilter] = useState<MetricKey | null>(persistedStatusFilter || null);

  const [searchDraft, setSearchDraft] = useState("");

  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownPlatformId, setDrilldownPlatformId] = useState<string | null>(null);

  const [timeMenuAnchorEl, setTimeMenuAnchorEl] = useState<null | HTMLElement>(null);
  const timeMenuOpen = Boolean(timeMenuAnchorEl);

  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(persistedAutoRefresh || 0);

  const widgets = data?.platformWidgets ?? [];

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  const location = useLocation();

  const handleOpenDrilldown = (platformId: string) => {
    setDrilldownPlatformId(platformId);
    setDrilldownOpen(true);
  };

  const handleCloseDrilldown = () => {
    setDrilldownOpen(false);
    setDrilldownPlatformId(null);
  };

  useEffect(() => {
    if (location.state?.platformId) {
      handleOpenDrilldown(location.state.platformId);
    }
  }, [location.state]);

  const openTimeMenu = (e: React.MouseEvent<HTMLElement>) => setTimeMenuAnchorEl(e.currentTarget);
  const closeTimeMenu = () => setTimeMenuAnchorEl(null);

  const cleanupAfterFetch = () => {
    if (!isMountedRef.current) return;

    setLoading(false);
    setUserActionFetching(false);
    isInitialFetchRef.current = false;
  };

  const fetchData = useCallback(async () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;

    const showGlobalLoader = !action;

    if (isMountedRef.current && action) setUserActionFetching(true);
    if (isMountedRef.current && isInitialFetchRef.current && showGlobalLoader) setLoading(true);

    try {
      const bodySearchText = isBackendSearch(latestSearchTextRef.current)
        ? latestSearchTextRef.current
        : "";
      const body = { searchText: bodySearchText, ...buildTimeRangeBody(timeRange, customStartDate, customEndDate) };
      if (!isMountedRef.current) return;

      if (dashboardConfigCache && isMountedRef.current) {
        const platformWidgets = new Array(dashboardConfigCache.totalPlatforms).fill(null);
        setData({
          meta: {
            generatedAt: new Date().toISOString(),
            timeRange,
            startDate: undefined,
            availableDates: dashboardConfigCache.availableDates,
            platformsInfo: dashboardConfigCache.platforms,
          },
          platformWidgets,
        });
        setLoading(false);
      }

      await getPlatformHealthSummary(body, (chunk) => {
        if (!isMountedRef.current) return;
        // Dashboard chunk
        if (chunk.type === "dashboard") {
          // Create loading slots based on platforms array with order and platformType
          const platformWidgets = new Array(
            chunk.dashboard.totalPlatforms
          ).fill(null);

          const newData = {
            meta: {
              generatedAt: new Date().toISOString(),
              timeRange,
              startDate: undefined,
              availableDates: chunk.dashboard.availableDates ?? [],
              platformsInfo: chunk.dashboard.platforms, 
            },
            platformWidgets,
          };
          setData(newData);

          // Cache the structural shape for future fetches
          dashboardConfigCache = {
            totalPlatforms: chunk.dashboard.totalPlatforms,
            platforms: chunk.dashboard.platforms,
            availableDates: chunk.dashboard.availableDates ?? [],
          };

          if (!bodySearchText) {
            defaultDataRef.current = newData;
          }

          setLoading(false);
          return;
        }

        // Platform chunk
        if (chunk.type === "alertPlatform" || chunk.type === "realtimePlatform") {
          const widget = mapResponseToPlatformWidgets(chunk)[0];
          if (!widget) return;

          setData((prev: any) => {
            if (!prev) return prev;

            const widgets = [...prev.platformWidgets];
            widgets[widget.order - 1] = widget;

            return {
              ...prev,
              meta: {
                ...prev.meta,
                startDate:
                  prev.meta.startDate ?? chunk.platform?.startDate,
              },
              platformWidgets: widgets,
            };
          });
        }
      });

      if (!isMountedRef.current) return;
    } catch (err) {
      console.error("Error loading platform health data:", err);
    } finally {
      cleanupAfterFetch();
    }
  }, [timeRange, customStartDate, customEndDate]);

  const selectTimeRange = (nextRange: string, start?: Date, end?: Date) => {
    setLoading(true);
    setTimeRange(nextRange);

    if (nextRange === "custom" && start && end) {
      setCustomStartDate(start);
      setCustomEndDate(end);
    } else {
      setCustomStartDate(null);
      setCustomEndDate(null);
    }

    updateTimeRange(nextRange, start, end);
    setActiveStatusFilter(null);
    updateStatusFilter(null);
    defaultDataRef.current = null;
    closeTimeMenu();
    setTriggerFetch((prev) => prev + 1);
  };

  const handleAutoRefreshChange = (minutes: number) => {
    setAutoRefreshInterval(minutes);
    updateAutoRefresh(minutes);
    closeTimeMenu();

    if (minutes > 0) {
      fetchData();
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (settingsLoaded) {
      fetchData();
    }
  }, [triggerFetch, settingsLoaded]);

   useEffect(() => {
    if (settingsLoaded) {
      setTimeRange(persistedTimeRange || DEFAULT_TIME_RANGE);
      setSearchText(persistedSearchText || "");
      setCustomStartDate(persistedCustomStartDate || null);
      setCustomEndDate(persistedCustomEndDate || null);
      setActiveStatusFilter(persistedStatusFilter || null);
      setAutoRefreshInterval(persistedAutoRefresh || 0);
      setSearchDraft(persistedSearchText || "");
    }
  }, [
    settingsLoaded,
    persistedTimeRange,
    persistedSearchText,
    persistedCustomStartDate,
    persistedCustomEndDate,
    persistedStatusFilter,
    persistedAutoRefresh,
  ]);

  useEffect(() => {
    setSearchDraft(searchText);
  }, [searchText]);

  useEffect(() => {
    if (!autoRefreshInterval || autoRefreshInterval <= 0) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      timeoutId = setTimeout(async () => {
        if (cancelled) return;
        await fetchData();
        if (!cancelled) {
          scheduleNext();
        }
      }, autoRefreshInterval * 60 * 1000);
    };

    scheduleNext();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [autoRefreshInterval, fetchData]);

  const selectedWidget = useMemo(() => {
    return widgets.find((w) => w && w.platformId === drilldownPlatformId) ?? null;
  }, [widgets, drilldownPlatformId]);

  const aggregate = useMemo(() => {
    return buildSummaryAggregate(widgets ?? null);
  }, [widgets]);

  const visibleWidgets = useMemo(() => {
     // Normal view => preserve slot positions
    if (!activeStatusFilter) {
      return widgets;
    }

    // For severity filtering, work only with loaded widgets
    let filtered = widgets.filter(
      (w): w is PlatformWidget => w !== null
    );

    filtered = filtered.filter((w) => {
      if (activeStatusFilter === "PLATFORM_AFFECTED") {
        return w && deriveStatusKey(w) !== "HEALTHY";
      }

      const counts = w?.alertCounts;
      if (!counts) return false;
      if (activeStatusFilter === SEVERITY.CRITICAL) return (counts.critical || 0) > 0;
      if (activeStatusFilter === SEVERITY.MAJOR) return (counts.major || 0) > 0;
      if (activeStatusFilter === SEVERITY.MINOR) return (counts.minor || 0) > 0;
      if (activeStatusFilter === SEVERITY.WARNING) return (counts.warning || 0) > 0;
      if (activeStatusFilter === SEVERITY.INFO) return (counts.info || 0) > 0;
      return false;
    });

    filtered.sort((a, b) => {
      const topA = getTopSegments(a);
      const topB = getTopSegments(b);

      const domA = getDominantTopSeg(a);
      const domB = getDominantTopSeg(b);

      const domIsClickedA = domA?.statusKey === activeStatusFilter ? 1 : 0;
      const domIsClickedB = domB?.statusKey === activeStatusFilter ? 1 : 0;
      if (domIsClickedB !== domIsClickedA) return domIsClickedB - domIsClickedA;

      const clickedCountA = topA.find((s) => s.statusKey === activeStatusFilter)?.count ?? 0;
      const clickedCountB = topB.find((s) => s.statusKey === activeStatusFilter)?.count ?? 0;
      if (clickedCountB !== clickedCountA) return clickedCountB - clickedCountA;

      return (a.platformName ?? "").localeCompare(b.platformName ?? "");
    });

    return filtered;
  }, [activeStatusFilter, widgets]);

  const hasSearchMatch = useMemo(() => {
    if (!searchText.trim()) return true;
    const query = searchText.trim().toLowerCase();
    return visibleWidgets.some((w) => {
      if (!w) return false;
      return widgetMatchesSearch(w, query);
    });
  }, [visibleWidgets, searchText]);

  const cloudWidgets = useMemo(
    () => {
      return visibleWidgets.filter((w, idx) => {
        // For loading slots (null), check platformsInfo metadata
        if (w === null) {
          const platformInfo = data?.meta?.platformsInfo?.[idx];
          return platformInfo?.platformType === "Cloud";
        }
        // For actual widgets, filter by group field
        const g = (w.group || "").toUpperCase();
        return g === "CLOUD";
      });
    },
    [visibleWidgets, data?.meta?.platformsInfo]
  );

  const onPremWidgets = useMemo(
    () => {
      return visibleWidgets.filter((w, idx) => {
        // For loading slots (null), check platformsInfo metadata
        if (w === null) {
          const platformInfo = data?.meta?.platformsInfo?.[idx];
          return platformInfo?.platformType === "On-prem";
        }
        // For actual widgets, filter by group field
        const g = (w.group || "").toUpperCase();
        return g === "ON PREM" || g === "ON-PREM" || g === "ON PREMISE";
      });
    },
    [visibleWidgets, data?.meta?.platformsInfo]
  );

  const { cloudPlaceholderCount, onPremPlaceholderCount } = useMemo(() => {
    // If we have loading slots (nulls) visible, don't add extra placeholders
    const hasLoadingSlots = visibleWidgets.includes(null);
    
    if (hasLoadingSlots || activeStatusFilter || searchText.trim()) {
      return { cloudPlaceholderCount: 0, onPremPlaceholderCount: 0 };
    }

    if (!isLgUp) {
      const columnsWhenNotLg = isSmUp ? 2 : 1;
      const columns = isMdUp ? 3 : columnsWhenNotLg;
      return {
        cloudPlaceholderCount: getSectionPlaceholderCount(cloudWidgets.length, columns),
        onPremPlaceholderCount: getSectionPlaceholderCount(onPremWidgets.length, columns),
      };
    }

    const cloudRows = Math.ceil(cloudWidgets.length / 3);
    const onPremRows = onPremWidgets.length;
    const totalRows = Math.max(cloudRows, onPremRows);

    if (totalRows === 0) {
      return { cloudPlaceholderCount: 0, onPremPlaceholderCount: 0 };
    }

    const cloudUsedInLastRow =
      cloudRows === totalRows && cloudWidgets.length > 0 ? (cloudWidgets.length % 3 || 3) : 0;

    const onPremUsedInLastRow =
      onPremRows === totalRows && onPremWidgets.length > 0 ? 1 : 0;

    return {
      cloudPlaceholderCount: 3 - cloudUsedInLastRow,
      onPremPlaceholderCount: 1 - onPremUsedInLastRow,
    };
  }, [cloudWidgets.length, onPremWidgets.length, isLgUp, isMdUp, isSmUp, activeStatusFilter, searchText, visibleWidgets]);

    const toggleMetric = (k: MetricKey | null) => {
    if (k === null) {
      setActiveStatusFilter(null);
      updateStatusFilter(null);
    } else {
      const newFilter = activeStatusFilter === k ? null : k;
      setActiveStatusFilter(newFilter);
      updateStatusFilter(newFilter);
    }
  };

    const handleSearchGo = () => {
    const finalSearchText = searchDraft.trim();

    if (finalSearchText === searchText) return;

    const neededApiBefore = isBackendSearch(searchText);
    const needsApiNow = isBackendSearch(finalSearchText);

    if (neededApiBefore || needsApiNow) {
      pendingActionRef.current = "search";
      setTriggerFetch((prev) => prev + 1);
    }

    setSearchText(finalSearchText);
    updateSearch(finalSearchText);
    setActiveStatusFilter(null);
    updateStatusFilter(null);
  };

    const handleSearchClear = () => {
    if (searchText === "") {
      setSearchDraft("");
    } else {
      const neededApiBefore = isBackendSearch(searchText);

      if (neededApiBefore) {
        if (defaultDataRef.current) {
          setData(defaultDataRef.current);
        }
        pendingActionRef.current = "clearIcon";
        setTriggerFetch((prev) => prev + 1);
      }

      setSearchDraft("");
      setSearchText("");
      updateSearch("");
      setActiveStatusFilter(null);
      updateStatusFilter(null);
    }
  };

  const gridItemSx = {
    display: "flex",
    minWidth: 0,
    "& > *": {
      width: "100%",
      height: "100%",
      minWidth: 0,
      maxWidth: "100%",
      boxSizing: "border-box",
    },
  };

  const emptyStateSx = {
    p: 2,
    borderRadius: "5px",
    background: "rgba(0,0,0,0.04)",
    color: "#060505ff",
    opacity: 0.85,
    fontSize: "13px",
    width: "100%",
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Box mt="80px" bgcolor="backgroundLight.primary">
          <PlatformHealthMetricsPortal
            aggregate={aggregate}
            activeStatusFilter={activeStatusFilter}
            toggleMetric={toggleMetric}
            openTimeMenu={openTimeMenu}
            timeMenuAnchorEl={timeMenuAnchorEl}
            timeMenuOpen={timeMenuOpen}
            closeTimeMenu={closeTimeMenu}
            timeRange={timeRange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            selectTimeRange={selectTimeRange}
            autoRefreshInterval={autoRefreshInterval}
            onAutoRefreshChange={handleAutoRefreshChange}
            searchDraft={searchDraft}
            setSearchDraft={setSearchDraft}
            handleSearchGo={handleSearchGo}
            handleSearchClear={handleSearchClear}
            searchText={searchText}
            startDateFromBackend={data?.meta?.startDate}
            availableDates={data?.meta?.availableDates}
          />

          <WidgetCardContainer bgColor="#fff" sx={{ backgroundColor: "#fff" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  flex: { xs: "1 1 100%", lg: "0 0 75%" },
                  width: { xs: "100%", lg: "75%" },
                  minWidth: 0,
                  boxSizing: "border-box",
                  pl: 1,
                  pr: { xs: 0, lg: 2 },
                  mb: { xs: 3, lg: 0 },
                }}
              >
                <Box sx={dashboardSectionHeaderSx}>
                  <Typography sx={dashboardHeaderTextSx}>Cloud</Typography>
                </Box>

                <Grid container rowSpacing={3} columnSpacing={3} alignItems="stretch">
                  {cloudWidgets.map((w, idx) => (
                    <Grid
                      key={w ? w.platformId : `cloud-loading-${idx}`}
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={4}   
                      sx={gridItemSx}
                    >
                      {w ? (
                        <PlatformHealthWidget
                          data={w}
                          onOpenDrilldown={handleOpenDrilldown}
                          searchText={searchText}
                          isKPI={w.isRealTime}
                          kpiItems={w.kpiItems}
                          allKpiItems={w.allKpiItems}
                        />
                      ) : (
                        <PlatformHealthWidgetSkeleton />
                      )}
                    </Grid>
                  ))}

                  {Array.from(
                    { length: cloudPlaceholderCount },
                    (_, i) => `cloud-placeholder-${cloudPlaceholderCount}-${i}`
                  ).map((key) => (
                    <Grid
                      key={key}
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={4}  
                      sx={gridItemSx}
                    >
                      <Box sx={placeholderCardSx}>No Platform</Box>
                    </Grid>
                  ))}

                  {cloudWidgets.filter((w) => w !== null).length === 0 && 
                    !userActionFetching && 
                    cloudPlaceholderCount === 0 && 
                    cloudWidgets.length === 0 && (
                    <Grid item xs={12}>
                      <Box sx={emptyStateSx}>
                        {searchText.trim() && !hasSearchMatch
                          ? "No results found"
                          : "No platforms available"}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Box
                sx={{
                  display: { xs: "none", lg: "block" },
                  width: "1px",
                  alignSelf: "stretch",
                  backgroundColor: "rgba(0,0,0,0.12)",
                  flexShrink: 0,
                  minHeight: "100%",
                }}
              />

              <Box
                sx={{
                  flex: { xs: "1 1 100%", lg: "0 0 25%" },
                  width: { xs: "100%", lg: "25%" },
                  minWidth: 0,
                  boxSizing: "border-box",
                  pl: { xs: 0, lg: 2 },
                }}
              >
                <Box sx={dashboardSectionHeaderSx}>
                  <Typography sx={dashboardHeaderTextSx}>On Prem</Typography>
                </Box>

                <Grid container rowSpacing={3} columnSpacing={3} alignItems="stretch">
                  {onPremWidgets.map((w, idx) => (
                    <Grid
                      key={w ? w.platformId : `onprem-loading-${idx}`}
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={12}  
                      sx={gridItemSx}
                    >
                      {w ? (
                        <PlatformHealthWidget
                          data={w}
                          onOpenDrilldown={handleOpenDrilldown}
                          searchText={searchText}
                          isKPI={w.isRealTime}
                          kpiItems={w.kpiItems}
                          allKpiItems={w.allKpiItems}
                        />
                      ) : (
                        <PlatformHealthWidgetSkeleton />
                      )}
                    </Grid>
                  ))}

                  {Array.from(
                    { length: onPremPlaceholderCount },
                    (_, i) => `onprem-placeholder-${onPremPlaceholderCount}-${i}`
                  ).map((key) => (
                    <Grid
                      key={key}
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={12}  
                      sx={gridItemSx}
                    >
                      <Box sx={placeholderCardSx}>No Platform</Box>
                    </Grid>
                  ))}

                  {onPremWidgets.filter((w) => w !== null).length === 0 &&
                    !userActionFetching &&
                    onPremPlaceholderCount === 0 &&
                    onPremWidgets.length === 0 && (
                      <Grid item xs={12}>
                        <Box sx={emptyStateSx}>
                          {searchText.trim() && !hasSearchMatch
                            ? "No results found"
                            : "No platforms available"}
                        </Box>
                      </Grid>
                    )}
                </Grid>
              </Box>
            </Box>
          </WidgetCardContainer>

          <PlatformHealthModal
            open={drilldownOpen}
            platformId={selectedWidget?.platformId ?? null}
            platformName={selectedWidget?.platformName}
            timeRangeType={timeRange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onClose={handleCloseDrilldown}
            initialSearchText=""
            timezone="CST"
          />
        </Box>
      )}
    </>
  );
};

export default PlatformHealthDashboard;